<template>
  <div>
    <ValidationObserver ref="observer">
      <v-title>
        Edit Items
        <v-btn small @click="addItem">Add</v-btn>
      </v-title>
      <ag-grid-vue
        :style="{ width, height }"
        domLayout="autoHeight"
        class="ag-theme-alpine mb-4"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :editType="'fullRow'"
        :rowData="filteredData"
        pagination
        :paginationPageSize="10"
        :frameworkComponents="frameworkComponents"
        @grid-ready="resizeGrid"
        @rowDataChanged="resizeGrid"
        @pagination-changed="resizeGrid"
      ></ag-grid-vue>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapActions, createNamespacedHelpers } from 'vuex';
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import TableActions from '@/components/admin/fieldOptions/tableActions';
import { TermItem } from '@/models/termItem';

const {
  mapActions: mapTermsConditionsActions,
  mapMutations: mapTermsConditionsMutations,
} = createNamespacedHelpers(`orders/termsConditions`);

export default {
  name: 'EditItem',
  props: {
    termConditionID: {
      type: Number,
    },
    editedSectionIndex: Number,
    section: String,
    isAdding: Boolean,
    termItems: {
      type: Array
    }
  },
  components: { AgGridVue, TableActions },
  data() {
    return {
      editData: [],
      editingIndex: -1,
      headers: [
        {
          text: 'SubHeader',
          align: 'left',
          sortable: false,
          value: 'subHeader',
          width: '30%',
        },
        {
          text: 'ItemText',
          align: 'left',
          sortable: false,
          value: 'item',
          width: '60%',
        },
        {
          text: 'ItemOrder',
          align: 'right',
          sortable: false,
          value: 'itemOrder',
          width: '5%',
        },
        {
          text: 'Actions',
          align: 'right',
          sortable: false,
          value: 'actions',
          width: '5%',
        },
      ],
      gridOptions: null,
      columnDefs: null,
      defaultColDef: null,
      rowData: null,
      gridOptions: null,
      frameworkComponents: null,
      height: '100%',
      width: '100%',
    };
  },
  methods: {
    ...mapActions('orders/termsConditions', [
      'addTermItem',
      'editTermItem',
      'removeTermItem',
    ]),
    ...mapTermsConditionsMutations(['removeItemTerm']),
    terms(items) {
      this.editData = [...items];
    },
    setToEditing(props) {
      this.editingIndex = props.index;
      this.$emit('editing');
    },
    isEditing(props) {
      return this.editingIndex == props.index;
    },
    save(item) {
      let vm = this;
      item.termsConditionsID = vm.termConditionID;
      vm.gridOptions.api.stopEditing();

      if (this.isAdding) {
        vm.addTermItem(item).then((data) => {
          vm.$emit('saved');
          vm.editingIndex = -1;
          let refreshPayload = { termItem: data, isDelete: false };
          if (!item.id)
            refreshPayload = {
              termItem: data,
              isDelete: false,
              isNew: true,
            };
          else refreshPayload = { termItem: data, isDelete: false };
          this.$emit('refresh', refreshPayload);
        });
      } else {
        vm.editTermItem(item).then((data) => {
          vm.$emit('saved');
          vm.editingIndex = -1;
          let refreshPayload = { termItem: data, isDelete: false };
          if (!item.id)
            refreshPayload = {
              termItem: data,
              isDelete: false,
              isNew: true,
            };
          else refreshPayload = { termItem: data, isDelete: false };
          this.$emit('refresh', refreshPayload);
        });
      }
    },
    deleteItem(id) {
      let vm = this;
      let answer = confirm('Are you sure you want to delete this item?');
      if (answer) {
        let payload = {
          termItemIndex: id,
          sectionIndex: vm.editedSectionIndex,
        };
        vm.removeTermItem(payload).then((data) => {
          let refreshPayload = { termItem: data, isDelete: true };
          this.$emit('refresh', refreshPayload);
        });
      }
    },
    addItem() {
      let vm = this;
      let temp = vm.termItems.map((x) => x.itemOrder);
      let order = temp.length > 0 ? Math.max(...temp) + 1 : 1;
      vm.termItems.push({ item: null, subHeader: 'General', itemOrder: order });

      vm.$nextTick(() => {
        let index = vm.termItems.length - 1;
        vm.gridOptions.api.setFocusedCell(index, 'subHeader');
        vm.gridOptions.api.startEditingCell({
          rowIndex: index,
          colKey: 'subHeader',
        });
      });
    },

    resizeGrid(grid) {
      let vm = this;
      vm.gridOptions = grid;
      grid.api.sizeColumnsToFit();
    },
  },
  computed: {
    filteredData: function() {
      if(this.termItems.length == 0) return []

      return this.termItems
    }
  },
  beforeMount() {
    let vm = this;
    vm.gridOptions = {};
    vm.columnDefs = [
      {
        headerName: 'Sub Header',
        field: 'subHeader',
        sortable: true,
        maxWidth: 200,
      },
      {
        headerName: 'Item Text',
        field: 'item',
        sortable: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorParams: {
          maxLength: 1000,
        },
      },
      {
        headerName: 'Item Order',
        field: 'itemOrder',
        sortable: true,
        maxWidth: 120,
        sort: 'asc'
      },
      {
        headerName: 'Actions',
        field: 'editAction',
        maxWidth: 120,
        editable: false,
        cellRenderer: 'tableActions',
        cellRendererParams: {
          clicked: function (param) {
            switch (param.type) {
              case 'save':
                vm.save(param);
                break;
              case 'delete':
                vm.deleteItem(param.id);
                break;
            }
          },
        },
      },
    ];

    vm.defaultColDef = {
      editable: true,
      wrapText: true,
      autoHeight: true,
    };

    vm.frameworkComponents = {
      tableActions: TableActions,
    };
  },
  mounted() {
    this.editData = [...this.termItems]
  },
};
</script>

<style scoped>
td .v-input .v-input__control .v-input__slot .v-text-field__slot input {
  margin-top: 0 !important;
}
</style>